import { useState, useMemo, useEffect } from 'react';
import { useRouter } from 'next/router';

import { ModalsDispatchContext, ModalsStateContext } from './ModalContext';

type ModalPropsType = {
  title?: string | React.ReactElement;
  content?: string;
  submitLabel?: string;
  cancelLabel?: string;
  onClose?: Function;
  onClickSubmit?: Function;
  onClickCancel?: Function;
  onClickClose?: Function;
  children?: React.ReactNode;
  needCloseButton?: boolean;
  scrollFix?: boolean;
  Component: React.ReactNode;
};

type ModalsProviderPropsType = {
  children: React.ReactNode;
};

type ModalType = {
  Component: () => React.ReactElement;
  props: ModalPropsType;
};

const ModalsProvider = ({
  children,
}: ModalsProviderPropsType): React.ReactElement => {
  const [openedModals, setOpenedModals] = useState([]);
  const router = useRouter();

  const open = (
    Component: (props: any) => React.ReactElement,
    props: ModalPropsType,
  ): void => {
    setOpenedModals((modals: any): any => {
      const openModals = modals.filter(
        // 다른 modal component가 열렸을때 'Toast'가 다시 렌더되는 이슈로 filter 추가됨.
        (modal: any) => {
          return modal.Component.displayName !== 'Toast';
        },
      );

      return [...openModals, { Component, props }];
    });
  };

  const close = (Component: (props: any) => React.ReactElement): void => {
    setOpenedModals((modals) => {
      return modals.filter((modal: ModalType) => {
        return modal.Component !== Component;
      });
    });
  };

  const closeAll = () => {
    setOpenedModals([]);
  };

  const dispatch = useMemo(() => ({ open, close }), []);

  useEffect(() => {
    router.events.on('routeChangeStart', closeAll);

    return () => router.events.off('routeChangeStart', closeAll);
  }, []);

  return (
    <ModalsStateContext.Provider value={openedModals}>
      <ModalsDispatchContext.Provider value={dispatch}>
        {children}
      </ModalsDispatchContext.Provider>
    </ModalsStateContext.Provider>
  );
};

export default ModalsProvider;
